/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useCallback, useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, Cell } from "recharts";
import { Scale, RefreshCw } from "lucide-react";

const VERDICT = { lenient: { zh: "偏松", en: "Lenient", cls: "bg-amber-100 text-amber-800" }, strict: { zh: "偏严", en: "Strict", cls: "bg-sky-100 text-sky-800" }, aligned: { zh: "一致", en: "Aligned", cls: "bg-emerald-100 text-emerald-800" } };

/* 我的评分校准：本人星级(×20) 与 AI 评分偏差的周趋势 + 近 30 天总览（评分前参考） */
export function RatingSelfCheck({ api, zh }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/rating-calibration/me", { params: { weeks } }); setData(d); }
    catch { setData(null); }
  }, [api, weeks]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const o = data.overall;
  const chart = data.series.map((w) => ({ ...w, label: w.week.slice(5), value: w.bias ?? 0 }));
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="rating-self-check" data-verdict={o?.verdict || ""}>
      <div className="flex items-center gap-2 px-4 py-3 flex-wrap">
        <Scale className="h-4 w-4 text-sky-600" />
        <h2 className="text-sm font-semibold text-slate-800">{zh ? "我的评分校准" : "My rating calibration"}</h2>
        {o ? (
          <span className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap" data-testid="rating-self-summary">
            {zh ? `近 30 天 ${o.ratings} 次评分 · ${o.pairs} 通可与 AI 对比 · 平均 ${o.avgStars ?? "—"} ★ · 偏差` : `30d: ${o.ratings} ratings · ${o.pairs} pairs · bias`}
            <b className={o.bias == null ? "text-slate-400" : o.bias >= 15 ? "text-amber-700" : o.bias <= -15 ? "text-sky-700" : "text-emerald-700"}>{o.bias == null ? "—" : `${o.bias > 0 ? "+" : ""}${o.bias}`}</b>
            {o.verdict ? <span className={`rounded-full px-2 py-0.5 font-semibold ${VERDICT[o.verdict].cls}`} data-testid="rating-self-verdict">{zh ? VERDICT[o.verdict].zh : VERDICT[o.verdict].en}</span> : <span className="text-slate-400">{zh ? "样本不足（需 ≥3 通）" : "n/a"}</span>}
          </span>
        ) : <span className="text-xs text-slate-400" data-testid="rating-self-empty">{zh ? "近 30 天暂无评分记录 · 先去坐席详情给录音打星吧" : "No ratings in the last 30 days"}</span>}
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-1">
          {[4, 8, 12].map((n) => <button key={n} data-testid={`rating-self-weeks-${n}`} onClick={() => setWeeks(n)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2 py-1 text-xs rounded-md font-medium ${weeks === n ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`)}>{n}{zh ? "周" : "w"}</button>)}
          <button data-testid="rating-self-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1.5 text-slate-400 hover:text-slate-700"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {data.tip && <p className="px-4 pb-2 text-xs text-slate-600 bg-sky-50/60 border-y border-sky-100 py-2" data-testid="rating-self-tip">{data.tip}</p>}
      <div className="px-2 pt-2 pb-1" data-testid="rating-self-chart">
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={chart} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis domain={[(min) => Math.min(-20, Math.floor(min) - 5), (max) => Math.max(20, Math.ceil(max) + 5)]} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v, _n, p) => [p.payload.bias == null ? (zh ? "无对比样本" : "no pairs") : `${v > 0 ? "+" : ""}${v}（${p.payload.pairs} ${zh ? "通" : "pairs"}）`, zh ? "偏差" : "Bias"]} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <ReferenceLine y={data.threshold} stroke="#f59e0b" strokeDasharray="4 4" />
            <ReferenceLine y={-data.threshold} stroke="#4F6BFF" strokeDasharray="4 4" />
            <Bar dataKey="value" radius={[3, 3, 0, 0]}>
              {chart.map((w) => <Cell key={w.week} fill={w.bias == null ? "#e2e8f0" : w.bias >= 15 ? "#f59e0b" : w.bias <= -15 ? "#0ea5e9" : "#10b981"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="px-4 pb-3 text-[11px] text-slate-400">{zh ? "偏差 = 星级 × 20 − 同一通话 AI 话术评分；橙线 +15 以上偏松、蓝线 −15 以下偏严。评分对话框会同步显示本提示。" : "Bias = stars×20 − AI score; above +15 lenient, below −15 strict."}</p>
    </div>
  );
}
